import {
  Box,
  Heading,
  Stack,
  List,
  ListItem,
  ListIcon,
  Text,
  HStack,
} from "@chakra-ui/react";
import React, { useContext } from "react";
import CurrencyContext from "../../context/CurrencyContext";
import { Trans } from "@lingui/macro";
import { FaCcMastercard, FaInfo } from "react-icons/fa";
import { FiUsers } from "react-icons/fi";
import { ParticipantFeeSummary } from "../../types/fee.types";
import { MultiUserMessageEscort } from "../common/MultiUserMessage";
import { formatPrice } from "../../utils/price-formatter";

interface EscortFeeSummaryProps {
  escortFee: ParticipantFeeSummary;
  originalEscortFee?: ParticipantFeeSummary;
}

export const EscortFeeSummary: React.FC<EscortFeeSummaryProps> = ({
  escortFee,
  originalEscortFee,
}) => {
  const currency = useContext(CurrencyContext);
  const hasChanges = originalEscortFee !== undefined;

  // Helper to check if a field changed
  const priceChanged =
    hasChanges &&
    originalEscortFee &&
    originalEscortFee.totalPrice !== escortFee?.totalPrice;
  const numberOfAttendantsChanged =
    hasChanges &&
    originalEscortFee &&
    originalEscortFee?.numberOfAttendants !== escortFee?.numberOfAttendants;
  const attendantsNameChanged =
    hasChanges &&
    originalEscortFee &&
    (originalEscortFee?.attendantsName || "") !==
      (escortFee?.attendantsName || "");
  const totalPriceChanged = priceChanged || numberOfAttendantsChanged;
  const hasAnyChanges =
    priceChanged || numberOfAttendantsChanged || attendantsNameChanged;
  return (
    <Box
      borderRadius={hasAnyChanges ? "lg" : "none"}
      _before={
        hasAnyChanges
          ? {
              content: '""',
              position: "absolute",
              inset: "0px",
              zIndex: -1,
              borderRadius: "lg",
              filter: "blur(4px)",
              pointerEvents: "none",
              boxShadow: `
                -3px -3px 8px rgba(245, 101, 101, 0.15),
                1.5px -2px 9px rgba(237, 100, 166, 0.15),
                -4.5px  1.5px 10px rgba(237, 137,  54, 0.15),
                1.5px  3px 8px rgba(237, 100, 166, 0.125),
                -3px 5.5px 8px rgba(245, 101, 101, 0.125),
                3px -3px 9px rgba(237, 137,  54, 0.125)
              `,
            }
          : undefined
      }
      zIndex={0}
      position="relative"
    >
      <Stack
        px={0}
        py={3}
        justifyContent={{
          base: "flex-start",
          md: "space-between",
        }}
        direction={{
          base: "column",
          md: "row",
        }}
        alignItems={{ md: "center" }}
      >
        <Heading size={"md"} w={{ md: "32%" }}>
          <Trans>Escort Fee</Trans>
        </Heading>
        <List spacing={3} textAlign="start" w={{ md: "37%" }}>
          {escortFee?.numberOfAttendants !== 0 && (
            <ListItem alignItems={"baseline"}>
              <HStack gap={0}>
                <ListIcon as={FaCcMastercard} color="gray.500" />
                <Text>
                  {priceChanged ? (
                    <>
                      <Text as="span" color="green.500" fontWeight={600}>
                        {formatPrice(escortFee.totalPrice)} {currency} /{" "}
                        <Trans>person</Trans>
                      </Text>
                      {originalEscortFee && (
                        <Text
                          as="span"
                          color="red.500"
                          ml={2}
                          textDecoration="line-through"
                        >
                          ({formatPrice(originalEscortFee.totalPrice)}{" "}
                          {currency} / <Trans>person</Trans>)
                        </Text>
                      )}
                    </>
                  ) : (
                    <>
                      {formatPrice(escortFee?.totalPrice)} {currency} /{" "}
                      <Trans>person</Trans>
                    </>
                  )}
                </Text>
              </HStack>
            </ListItem>
          )}
          <ListItem alignItems={"baseline"}>
            <HStack gap={0}>
              <ListIcon as={FaInfo} color="gray.500" />
              <Text>
                {numberOfAttendantsChanged ? (
                  <>
                    <Text as="span" color="green.500" fontWeight={600}>
                      <MultiUserMessageEscort
                        count={escortFee.numberOfAttendants}
                      />
                    </Text>
                    {originalEscortFee && (
                      <Text
                        as="span"
                        color="red.500"
                        ml={2}
                        textDecoration="line-through"
                      >
                        (
                        <MultiUserMessageEscort
                          count={originalEscortFee?.numberOfAttendants}
                        />
                        )
                      </Text>
                    )}
                  </>
                ) : (
                  <MultiUserMessageEscort
                    count={escortFee?.numberOfAttendants}
                  />
                )}
              </Text>
            </HStack>
          </ListItem>
          {escortFee?.numberOfAttendants !== 0 && (
            <ListItem alignItems={"baseline"}>
              <HStack gap={0}>
                <ListIcon as={FiUsers} color="gray.500" />
                <Text>
                  <Trans>Name of accompanying person(s)</Trans>:{" "}
                  {attendantsNameChanged ? (
                    <>
                      <Text as="span" color="green.500" fontWeight={600}>
                        {escortFee.attendantsName || "-"}
                      </Text>
                      {originalEscortFee && (
                        <Text
                          as="span"
                          color="red.500"
                          ml={2}
                          textDecoration="line-through"
                        >
                          ({originalEscortFee.attendantsName || "-"})
                        </Text>
                      )}
                    </>
                  ) : (
                    escortFee?.attendantsName || "-"
                  )}
                </Text>
              </HStack>
            </ListItem>
          )}
        </List>
        <Heading size={"xl"} w={{ md: "24%" }} textAlign={{ md: "end" }}>
          {totalPriceChanged ? (
            <>
              <Text as="span" color="green.500" fontWeight={600}>
                {formatPrice(
                  escortFee.totalPrice * escortFee.numberOfAttendants,
                )}{" "}
                {currency}
              </Text>
              {originalEscortFee && (
                <Text
                  as="span"
                  color="red.500"
                  ml={2}
                  textDecoration="line-through"
                  fontSize="lg"
                >
                  (
                  {formatPrice(
                    originalEscortFee.totalPrice *
                      originalEscortFee.numberOfAttendants,
                  )}{" "}
                  {currency})
                </Text>
              )}
            </>
          ) : (
            <>
              {formatPrice(
                escortFee?.totalPrice * escortFee?.numberOfAttendants,
              )}{" "}
              {currency}
            </>
          )}
        </Heading>
      </Stack>
    </Box>
  );
};
